import { forwardRef } from 'react';
import { cn, memoClassNames } from '@/utils';
import { cva, VariantProps } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';

const classes = memoClassNames('Carousel', ['root', 'slide', 'track']);

const trackVariant = cva('mm:flex', {
  variants: {
    spacing: {
      default: 'mm:-ml-4 mm:*:pl-4',
      sm: 'mm:-ml-2 mm:*:pl-2',
      lg: 'mm:-ml-6 mm:*:pl-6',
    },
  },
  defaultVariants: {
    spacing: 'default',
  },
});

export type CarouselProps = React.ComponentProps<'div'> &
  VariantProps<typeof trackVariant> & {
    asChild?: boolean;
  };

export const Carousel = forwardRef<HTMLDivElement, CarouselProps>(
  (props, ref) => {
    const { className, children, spacing, asChild, ...others } = props;
    const Comp = asChild ? Slot : 'div';

    return (
      <div
        {...others}
        className={cn(classes.root, 'mm:overflow-hidden', className)}
        ref={ref}
      >
        <Comp
          className={cn(
            classes.track,
            trackVariant({
              spacing,
            })
          )}
        >
          {children}
        </Comp>
      </div>
    );
  }
);

Carousel.displayName = 'Carousel';

export type CarouselSlideProps = React.ComponentProps<'div'> & {
  asChild?: boolean;
};

export const CarouselSlide = forwardRef<HTMLDivElement, CarouselSlideProps>(
  ({ className, ...others }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          classes.slide,
          'mm:grow-0 mm:shrink-0 mm:basis-full',
          className
        )}
        {...others}
      />
    );
  }
);
CarouselSlide.displayName = 'CarouselSlide';
